import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash2, X, FileSearch } from "lucide-react";
import { toast } from "sonner";

const KINDS = ["interface", "proxy_rule", "identity_policy"];
const empty = {
  name: "", target_kind: "interface", target_id: "eth0",
  bpf_filter: "tcp or udp", duration_seconds: 60, max_bytes: 50 * 1024 * 1024,
  description: "",
};
const STATUS_COLORS = {
  queued: "text-amber-400 border-amber-500/40 bg-amber-500/10",
  running: "text-cyan-400 border-cyan-500/40 bg-cyan-500/10",
  done: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  cancelled: "text-zinc-500 border-zinc-700",
  failed: "text-red-400 border-red-500/40 bg-red-500/10",
};

export default function PCap() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/pcap/jobs"); setItems(data); }
    catch (err) { console.error("pcap load failed", err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try { await api.post("/pcap/jobs", form); toast.success("Capture queued"); setOpen(false); setForm(empty); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cancel = async (id) => {
    try { await api.post(`/pcap/jobs/${id}/cancel`); toast.success("Cancelled"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this capture job?")) return;
    try { await api.delete(`/pcap/jobs/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Layout title="Packet Capture" subtitle="DIAGNOSTICS">
      <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3 mb-4 text-xs font-mono text-amber-300">
        ⚠ Control-plane only — capture daemon not yet wired. Jobs land in <code>pcap_jobs</code> as <span className="text-amber-400">queued</span>.
      </div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest" data-testid="pcap-count">{items.length} JOBS</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-pcap-btn" className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2"><Plus className="w-4 h-4" /> NEW CAPTURE</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-xl">
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><FileSearch className="w-4 h-4 text-cyan-400" /> Schedule Packet Capture</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2"><L>Name</L><Input data-testid="pcap-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
              <div><L>Target kind</L>
                <Select value={form.target_kind} onValueChange={(v) => setForm({ ...form, target_kind: v })}>
                  <SelectTrigger data-testid="pcap-kind-select" className="bg-zinc-900 border-zinc-800 rounded-sm font-mono uppercase text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    {KINDS.map((k) => <SelectItem key={k} value={k} className="font-mono uppercase text-xs">{k}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><L>Target id</L><Input data-testid="pcap-target-input" value={form.target_id} onChange={(e) => setForm({ ...form, target_id: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div className="col-span-2"><L>BPF filter</L><Input data-testid="pcap-bpf-input" value={form.bpf_filter} onChange={(e) => setForm({ ...form, bpf_filter: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" placeholder="tcp port 443 and host 10.0.0.0/8" /></div>
              <div><L>Duration (s)</L><Input data-testid="pcap-dur-input" type="number" value={form.duration_seconds} onChange={(e) => setForm({ ...form, duration_seconds: parseInt(e.target.value || "0", 10) })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div><L>Max bytes</L><Input data-testid="pcap-bytes-input" type="number" value={form.max_bytes} onChange={(e) => setForm({ ...form, max_bytes: parseInt(e.target.value || "0", 10) })} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" /></div>
              <div className="col-span-2"><L>Description</L><Input data-testid="pcap-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="submit-pcap-btn" onClick={submit} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">QUEUE</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="pcap-list">
        {items.map((j) => (
          <div key={j.job_id} data-testid={`pcap-card-${j.job_id}`} className="border border-zinc-800 rounded-sm bg-zinc-900/60 p-4 fade-up">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-display text-base font-bold tracking-tight">{j.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">{j.job_id} · {j.target_kind} {j.target_id}</div>
              </div>
              <span className={"text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm " + (STATUS_COLORS[j.status] || "text-zinc-400 border-zinc-700")}>{j.status}</span>
            </div>
            <div className="font-mono text-xs space-y-1 text-zinc-300">
              <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">FILTER</span><span className="text-cyan-400 break-all">{j.bpf_filter}</span></div>
              <div><span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2">DUR</span>{j.duration_seconds}s · <span className="text-zinc-500 uppercase tracking-widest text-[10px] mr-2 ml-2">MAX</span>{(j.max_bytes / 1024 / 1024).toFixed(0)} MB</div>
            </div>
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-800">
              {j.status === "queued" && <Button size="sm" variant="ghost" data-testid={`cancel-pcap-${j.job_id}`} onClick={() => cancel(j.job_id)} className="gap-1.5 text-amber-400"><X className="w-3.5 h-3.5" />Cancel</Button>}
              <Button size="sm" variant="ghost" data-testid={`delete-pcap-${j.job_id}`} onClick={() => del(j.job_id)} className="gap-1.5 text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" />Delete</Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-zinc-500 font-mono text-sm">no capture jobs.</div>}
      </div>
    </Layout>
  );
}

function L({ children }) { return <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{children}</Label>; }
